import { nextTick, onMounted, ref, Ref } from 'vue';
import {
  DragModel,
  GetByOwnerTasksRequestModel,
  GetByParentTasksRequestModel,
  GetByPerformerTasksRequestModel,
  GetByPriorityTasksRequestModel,
  GetByStatusTasksRequestModel,
  GetByTagTasksRequestModel,
  GetByTypeTasksRequestModel,
  GroupedTaskRequestModel,
  PageRequestModel,
  PageResponseModel,
  ResponseModel,
  TaskModel,
  TasksKanbanColumnModel,
  TasksKanbanFilterModel,
  TasksKanbanGroupingModel,
  TaskUpdatePriorityModel,
  TaskUpdateStatusModel,
  TaskUpdateTypeModel,
} from '../models';
import { TaskKanbanAdapterService } from '../adapter';
import { TasksKanbanGrouppingTypeEnum, TaskViewElementEnum } from '../enums';
import { debounce } from '../utils';
import { useRouter } from 'vue-router';

type KanbanManagerParams = {
  /**Список колонок */
  columns: Ref<Array<TasksKanbanColumnModel>>;
  /**Список задач */
  tasks: Ref<Array<TaskModel>>;
  /**Отступ снизу колонки для подгрузки задач по скроллу */
  bottomScrollOffset?: number;
  /**Кол-во задач получаемых первым запросом */
  firstRequestTakeCount?: number;
};

export const useKanbanManager = (_params: KanbanManagerParams) => {
  const params: Required<KanbanManagerParams> = {
    ..._params,
    bottomScrollOffset: _params.bottomScrollOffset ?? 200,
    firstRequestTakeCount: _params.firstRequestTakeCount ?? 10,
  };

  /**Адаптер */
  const adapter = new TaskKanbanAdapterService();
  /**Роутер */
  const router = useRouter();
  /**Фильтр */
  const filter = ref(new TasksKanbanFilterModel());
  /**Группировка */
  const grouppingType = ref(TasksKanbanGrouppingTypeEnum.Status);
  /**Отображаемые элементы */
  const viewElements = ref<Array<TaskViewElementEnum>>(Object.values(TaskViewElementEnum));
  /**Id очереди задач */
  let queueId: string;
  /**Загрузка */
  const isLoading = ref(false);
  /**Задача в процессе перемещения */
  let isMovingTask = false;

  /**
   * * Проверить проскроллен ли элемент до конца
   * @param element Элемент
   * @returns
   */
  const checkScrolledToBottom = (element: HTMLElement): boolean =>
    element.scrollTop + element.clientHeight + params.bottomScrollOffset >= element.scrollHeight;

  /**
   * * Проверить возможность подгрузки задач в колонку
   * @param scrollElement Элемент скролла
   * @param column Колонка
   * @returns
   */
  const checkLoadTasks = (scrollElement: HTMLElement, column: TasksKanbanColumnModel) =>
    checkScrolledToBottom(scrollElement) && column.CountTasks !== column.Data.length;

  const getTasksColumn = async (
    grouping: TasksKanbanGroupingModel,
    page: PageRequestModel,
  ): Promise<PageResponseModel<TaskModel>> => {
    let resp: PageResponseModel<TaskModel>;
    const baseRequest = {
      QueueTaskId: queueId,
      ViewSettings: {
        showNestedTasks: false,
        viewElements: viewElements.value,
      },
      Page: page,
      Search: filter.value.Search,
    };

    if (grouping.Author) {
      resp = await adapter.getByOwnerTaskList(
        new GetByOwnerTasksRequestModel({
          OwnerId: grouping.Author.Id,
          ...baseRequest,
        }),
      );
    } else if (grouping.Performer) {
      resp = await adapter.getByPerformerTaskList(
        new GetByPerformerTasksRequestModel({
          PerformerId: grouping.Performer.Id,
          ...baseRequest,
        }),
      );
    } else if (grouping.Priority) {
      resp = await adapter.getByPriorityTaskList(
        new GetByPriorityTasksRequestModel({
          PriorityId: grouping.Priority.Id,
          ...baseRequest,
        }),
      );
    } else if (grouping.Status) {
      resp = await adapter.getByStatusTaskList(
        new GetByStatusTasksRequestModel({
          StatusId: grouping.Status.Id,
          ...baseRequest,
        }),
      );
    } else if (grouping.Tag) {
      resp = await adapter.getByTagTaskList(
        new GetByTagTasksRequestModel({
          TagId: grouping.Tag.Id,
          ...baseRequest,
        }),
      );
    } else if (grouping.Type) {
      resp = await adapter.getByTypeTaskList(
        new GetByTypeTasksRequestModel({
          TypeId: grouping.Type.Id,
          ...baseRequest,
        }),
      );
    }

    return resp;
  };

  /**
   * * Подгрузить задачи в колонку
   * @param column колонка
   * @param checkLoad
   * @returns
   */
  const loadTasksToColumn = async (column: TasksKanbanColumnModel) => {
    if (column.IsLoading || !queueId) return;
    column.IsLoading = true;

    let resp: PageResponseModel<TaskModel> = await getTasksColumn(
      column.Grouping,
      new PageRequestModel({
        Skip: column.Data.length,
        Take: 10,
      }),
    );

    if (!resp.IsSuccess) return;

    params.tasks.value.push(...resp.Items);

    const isDrag = [
      TasksKanbanGrouppingTypeEnum.Status,
      TasksKanbanGrouppingTypeEnum.Priority,
      TasksKanbanGrouppingTypeEnum.Type,
    ].includes(grouppingType.value);

    column.Data.push(
      ...resp.Items.map(
        (task) =>
          new DragModel<{ TaskId: number }>({
            AllowDrag: isDrag,
            AllowDrop: isDrag,
            Data: {
              TaskId: task.Id,
            },
          }),
      ),
    );

    column.IsLoading = false;
  };

  /**
   * * При скролле колонки
   * @param event Событие
   * @param columnIndex Индекс колонки
   * @returns
   */
  const onScrollColumn = (event: any, columnIndex: number) => {
    const target = event.target as HTMLElement | undefined;
    const column = params.columns.value[columnIndex];

    if (!target || !column || !checkLoadTasks(target, column) || isMovingTask) return;

    loadTasksToColumn(column);
  };

  /**
   * * Загрузить все сгруппированные задачи
   * @param _grouppingType Тип группировки
   * @param queueId Id очереди
   */
  const loadAllGrouppedTasks = async (_queueId: string) => {
    isLoading.value = true;
    queueId = _queueId;

    const resp = await adapter.getGroupedTaskList(
      new GroupedTaskRequestModel({
        GrouppedBy: grouppingType.value,
        QueueTaskId: queueId,
        ColumnTakeCount: params.firstRequestTakeCount,
        ViewSettings: {
          showNestedTasks: filter.value.ShowNestedTasks,
          viewElements: viewElements.value,
        },
        Search: filter.value.Search,
      }),
    );
    if (!resp.IsSuccess) return;

    const isDrag = [
      TasksKanbanGrouppingTypeEnum.Status,
      TasksKanbanGrouppingTypeEnum.Priority,
      TasksKanbanGrouppingTypeEnum.Type,
    ].includes(grouppingType.value);

    params.columns.value = resp.Value.map((column) => {
      return new TasksKanbanColumnModel({
        Grouping: new TasksKanbanGroupingModel({
          Status: column.Status,
          Priority: column.Priority,
          Performer: column.Performer,
          Author: column.Author,
          Tag: column.Tag,
          Type: column.Type,
        }),
        CountTasks: column.Count,
        Data: column.Items.map(
          (task) =>
            new DragModel<{ TaskId: number }>({
              AllowDrag: isDrag,
              AllowDrop: isDrag,
              Data: {
                TaskId: task.Id,
              },
            }),
        ),
      });
    });

    params.tasks.value = resp.Value.reduce((acc, item) => {
      return [
        ...acc,
        ...item.Items.filter((_item) => !acc.some((accItem) => accItem.Id === _item.Id)),
      ];
    }, new Array<TaskModel>());
    isLoading.value = false;
  };

  const debounceLoadAllGrouppedTasks = debounce(loadAllGrouppedTasks, 300);

  /**
   * * Подгрузить подзадачи
   * @param taskId ID задачи
   * @returns
   */
  const loadSubtasks = async (taskId: number) => {
    if (!queueId) return;

    const task = params.tasks.value.find((task) => task.Id === taskId);

    if (!task) return;

    task.IsLoadingSubtasks = true;
    const resp = await adapter.getByParentTaskList(
      new GetByParentTasksRequestModel({
        QueueTaskId: queueId,
        ViewSettings: {
          showNestedTasks: false,
          viewElements: viewElements.value,
        },
        ParentId: taskId,
      }),
    );
    if (resp.IsSuccess && resp.Value) task.Subtasks = resp.Value;

    task.IsLoadingSubtasks = false;
  };

  /**
   * * Обновить задачи в колонке
   * @param column Колонка
   * @returns
   */
  const updateTasksInColumn = async (column: TasksKanbanColumnModel) => {
    let resp: PageResponseModel<TaskModel> = await getTasksColumn(
      column.Grouping,
      new PageRequestModel({
        Skip: 0,
        Take: Math.max(column.Data.length, params.firstRequestTakeCount),
      }),
    );

    if (!resp.IsSuccess) return;

    let tasks = params.tasks.value.filter(
      (task) => !resp.Items.some((_task) => task.Id === _task.Id),
    );
    tasks.push(...resp.Items);

    params.tasks.value = tasks;

    const isDrag = [
      TasksKanbanGrouppingTypeEnum.Status,
      TasksKanbanGrouppingTypeEnum.Priority,
      TasksKanbanGrouppingTypeEnum.Type,
    ].includes(grouppingType.value);

    column.Data = resp.Items.map(
      (task) =>
        new DragModel<{ TaskId: number }>({
          AllowDrag: isDrag,
          AllowDrop: isDrag,
          Data: {
            TaskId: task.Id,
          },
        }),
    );

    column.CountTasks = resp.Count;
  };
  /**
   * * Переместить задачу
   * @param taskId Id задачи
   * @param toColumn Куда переместить
   * @returns
   */
  const moveTask = async (
    taskId: number,
    toColumn: TasksKanbanColumnModel,
    fromColumn: TasksKanbanColumnModel,
  ) => {
    params.columns.value.forEach((column) =>
      column.Data.forEach((dragData) => {
        dragData.AllowDrag = false;
      }),
    );
    isMovingTask = true;
    const grouping = toColumn.Grouping;

    let resp: ResponseModel;

    if (grouping.Status) {
      resp = await adapter.updateStatus(
        new TaskUpdateStatusModel({
          TaskId: taskId,
          StatusId: grouping.Status.Id,
        }),
      );
    } else if (grouping.Priority) {
      resp = await adapter.updatePriority(
        new TaskUpdatePriorityModel({
          TaskId: taskId,
          PriorityId: grouping.Priority.Id,
        }),
      );
    } else if (grouping.Type) {
      resp = await adapter.updateTaskType(
        new TaskUpdateTypeModel({
          TaskId: taskId,
          TaskTypeId: grouping.Type.Id,
        }),
      );
    } else {
      return;
    }

    isMovingTask = false;
    params.columns.value.forEach((column) =>
      column.Data.forEach((dragData) => {
        dragData.AllowDrag = true;
      }),
    );
  };

  return {
    /**Группировка */
    grouppingType,
    /**Отображаемые элементы */
    viewElements,
    /**Фильтр */
    filter,
    /**Загрузка */
    isLoading,
    /**
     * * При скролле колонки
     * @param event Событие
     * @param columnIndex Индекс колонки
     * @returns
     */
    onScrollColumn,
    /**
     * * Загрузить все сгруппированные задачи
     * @param _grouppingType Тип группировки
     * @param queueId Id очереди
     */
    loadAllGrouppedTasks,
    debounceLoadAllGrouppedTasks,
    /**
     * * Подгрузить подзадачи
     * @param taskId ID задачи
     * @returns
     */
    loadSubtasks,
    /**
     * * Переместить задачу
     * @param taskId Id задачи
     * @param toColumn Куда переместить
     * @returns
     */
    moveTask,
    /**
     * * Обновить задачи в колонке
     * @param column Колонка
     * @returns
     */
    updateTasksInColumn,
  };
};
